import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { toast } from '../hooks/use-toast';
import { Megaphone, ArrowLeft, CheckCircle, XCircle, Plus, Pause, Play, Trash, CalendarPlus } from '@phosphor-icons/react';

const STATUS_STYLES = {
  pending_payment: 'bg-neutral-200 text-neutral-600', pending_approval: 'bg-amber-100 text-amber-800',
  active: 'bg-emerald-100 text-emerald-700', paused: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700', expired: 'bg-neutral-200 text-neutral-500',
};
const TABS = ['all', 'pending_approval', 'active', 'paused', 'rejected', 'expired'];

const EMPTY_FORM = { company_name: '', target_url: '', placement: 'homepage', weeks: 4, run_forever: false, target_trade: '', target_city: '', image_data: '' };

const AdminAds = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState(null);
  const [counts, setCounts] = useState({});
  const [revenue, setRevenue] = useState(0);
  const [tab, setTab] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState({ placements: [], trades: [], cities: [] });
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = tab === 'all' ? {} : { status: tab };
      const r = await axiosInstance.get('/ads/admin/list', { params });
      setAds(r.data.ads || []);
      setCounts(r.data.counts || {});
      setRevenue(r.data.revenue || 0);
    } catch (e) {
      if (e.response?.status === 403) navigate('/admin');
      else setAds([]);
    }
  }, [tab, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axiosInstance.get('/ads/placements').then((r) => setMeta({
      placements: r.data.placements || [], trades: r.data.trades || [], cities: r.data.cities || [],
    })).catch(() => {});
  }, []);

  const review = async (id, action, weeks) => {
    if (action === 'delete') {
      if (!window.confirm('Permanently delete this ad?')) return;
      try {
        await axiosInstance.delete(`/ads/admin/${id}`);
        toast({ title: 'Ad deleted' });
        load();
      } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
      return;
    }
    try {
      await axiosInstance.patch(`/ads/admin/${id}`, { action, ...(weeks ? { weeks } : {}) });
      const msgs = { approve: 'Ad approved — now live', reject: 'Ad rejected', pause: 'Ad paused', resume: 'Ad resumed', extend: `Extended ${weeks} week${weeks === 1 ? '' : 's'}` };
      toast({ title: msgs[action] || 'Updated' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast({ title: 'Image too large', description: 'Max 2 MB', variant: 'destructive' }); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_data: reader.result }));
    reader.readAsDataURL(file);
  };

  const generateBanner = async () => {
    if (aiPrompt.trim().length < 5) {
      toast({ title: 'Describe the banner first', description: 'e.g. "Summer sale on power tools, orange and black"', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    try {
      const r = await axiosInstance.post('/ads/admin/generate-image', {
        prompt: aiPrompt.trim(), company_name: form.company_name || null,
      }, { timeout: 120000 });
      setForm((f) => ({ ...f, image_data: r.data.image_data }));
      toast({ title: 'Banner generated ✨', description: 'Not happy? Tweak the description and generate again.' });
    } catch (e) {
      toast({ title: 'Generation failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setGenerating(false); }
  };

  const createAd = async () => {
    if (!form.company_name || !form.target_url || !form.image_data) {
      toast({ title: 'Missing fields', description: 'Name, target URL and banner image are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post('/ads/admin/create', {
        ...form,
        weeks: Number(form.weeks) || 4,
        target_trade: form.target_trade || null,
        target_city: form.target_city || null,
      });
      toast({ title: 'Ad is live 🎉' });
      setForm(EMPTY_FORM);
      setShowCreate(false);
      setTab('active');
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const selectedPlacement = meta.placements.find((p) => p.id === form.placement);

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-ads-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin')} className="text-white hover:text-[#D95800]" data-testid="ads-back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="w-12 h-12 bg-[#D95800] border-2 border-white flex items-center justify-center">
            <Megaphone size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white uppercase">Ad Manager</h1>
            <p className="text-neutral-400 text-sm">Review paid ads or place your own anywhere on the site</p>
          </div>
          <span className="ml-auto px-3 py-1.5 bg-emerald-600 text-white text-sm font-black rounded" data-testid="ads-revenue">
            ${revenue.toLocaleString()} revenue
          </span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="ads-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 h-10 text-sm font-black uppercase border-2 border-black ${tab === t ? 'bg-neutral-900 text-white' : 'bg-white hover:bg-neutral-50'}`}
              data-testid={`ads-tab-${t}`}>
              {t.replace('_', ' ')}{t !== 'all' && counts[t] ? ` (${counts[t]})` : ''}
            </button>
          ))}
          <button onClick={() => setShowCreate((s) => !s)}
            className="ml-auto h-10 px-4 flex items-center gap-1.5 text-sm font-black uppercase bg-[#D95800] text-white border-2 border-black shadow-[3px_3px_0px_#000]"
            data-testid="create-ad-toggle-btn">
            <Plus size={16} weight="bold" /> {showCreate ? 'Close' : 'Create Ad'}
          </button>
        </div>

        {showCreate && (
          <div className="bg-white border-2 border-black shadow-[4px_4px_0px_#000] p-5 mb-6" data-testid="create-ad-form">
            <p className="font-black uppercase mb-1">Place an ad directly</p>
            <p className="text-xs text-neutral-500 mb-4">House promos, sponsors, partners — goes live instantly on any placement, no payment needed.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Advertiser / campaign name" className="h-11 px-3 border-2 border-black text-sm font-semibold" data-testid="create-ad-name" />
              <input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                placeholder="Target URL (https://... or /marketplace)" className="h-11 px-3 border-2 border-black text-sm font-semibold" data-testid="create-ad-url" />
              <select value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value, target_trade: '', target_city: '' })}
                className="h-11 px-3 border-2 border-black text-sm font-semibold bg-white" data-testid="create-ad-placement">
                {meta.placements.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <div className="flex items-center gap-3">
                <input type="number" min="1" max="52" value={form.weeks} disabled={form.run_forever}
                  onChange={(e) => setForm({ ...form, weeks: e.target.value })}
                  className="h-11 px-3 border-2 border-black text-sm font-semibold w-24 disabled:opacity-40" data-testid="create-ad-weeks" />
                <span className="text-sm font-bold">weeks</span>
                <label className="flex items-center gap-1.5 text-sm font-bold cursor-pointer">
                  <input type="checkbox" checked={form.run_forever} onChange={(e) => setForm({ ...form, run_forever: e.target.checked })} data-testid="create-ad-forever" />
                  Run indefinitely
                </label>
              </div>
              {selectedPlacement?.trade_targetable && (
                <>
                  <select value={form.target_trade} onChange={(e) => setForm({ ...form, target_trade: e.target.value })}
                    className="h-11 px-3 border-2 border-black text-sm font-semibold bg-white" data-testid="create-ad-trade">
                    <option value="">All trades</option>
                    {meta.trades.map((t) => <option key={t.id} value={t.id}>{t.label} only</option>)}
                  </select>
                  <select value={form.target_city} onChange={(e) => setForm({ ...form, target_city: e.target.value })}
                    className="h-11 px-3 border-2 border-black text-sm font-semibold bg-white" data-testid="create-ad-city">
                    <option value="">All cities</option>
                    {meta.cities.map((c) => <option key={c.id} value={c.id}>{c.label} only</option>)}
                  </select>
                </>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 bg-violet-50 border-2 border-violet-300 p-3">
              <span className="text-xs font-black uppercase text-violet-700 shrink-0">✨ AI Banner</span>
              <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !generating && generateBanner()}
                placeholder='Describe it — e.g. "Pro-grade power tools sale, bold orange and black"'
                className="flex-1 min-w-[240px] h-11 px-3 border-2 border-black text-sm font-semibold" data-testid="ai-banner-prompt" />
              <button onClick={generateBanner} disabled={generating}
                className="h-11 px-4 text-sm font-black uppercase bg-violet-600 text-white border-2 border-black disabled:opacity-50"
                data-testid="ai-banner-generate-btn">
                {generating ? 'Generating (~20s)...' : 'Generate'}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="h-11 px-4 flex items-center text-sm font-black uppercase bg-neutral-100 border-2 border-black cursor-pointer hover:bg-neutral-200">
                {form.image_data ? 'Change banner image' : 'Upload banner image'}
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="create-ad-image-input" />
              </label>
              {form.image_data && <img src={form.image_data} alt="Banner preview" className="h-16 border border-neutral-300" data-testid="create-ad-preview" />}
              <button onClick={createAd} disabled={saving}
                className="ml-auto h-11 px-6 text-sm font-black uppercase bg-emerald-600 text-white border-2 border-black shadow-[3px_3px_0px_#000] disabled:opacity-50"
                data-testid="create-ad-submit">
                {saving ? 'Publishing...' : '🚀 Publish Ad'}
              </button>
            </div>
          </div>
        )}

        {ads?.length === 0 && (
          <div className="bg-white border-2 border-black p-10 text-center shadow-[4px_4px_0px_#000]" data-testid="no-ads">
            <Megaphone size={40} className="mx-auto text-neutral-300 mb-3" />
            <p className="font-black uppercase">No ads{tab !== 'all' ? ` in "${tab.replace('_', ' ')}"` : ' yet'}</p>
            <p className="text-sm text-neutral-500 mt-1">Companies buy ad space at /advertise — or hit Create Ad to place one yourself.</p>
          </div>
        )}

        <div className="space-y-4">
          {ads?.map((a) => (
            <div key={a.id} className="bg-white border-2 border-black shadow-[3px_3px_0px_#000] p-4" data-testid={`ad-row-${a.id}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className={`px-2 py-0.5 text-xs font-black rounded uppercase ${STATUS_STYLES[a.status] || ''}`}>{a.status.replace('_', ' ')}</span>
                {a.price === 0 && <span className="px-2 py-0.5 text-xs font-black rounded uppercase bg-purple-100 text-purple-700" data-testid={`house-ad-tag-${a.id}`}>House Ad</span>}
                <span className="font-black">{a.company_name}</span>
                {a.target_trade_label && (
                  <span className="px-2 py-0.5 text-xs font-black rounded uppercase bg-blue-100 text-blue-800" data-testid={`ad-trade-tag-${a.id}`}>{a.target_trade_label}{a.target_city_label ? ` · ${a.target_city_label}` : ''} only</span>
                )}
                <span className="text-sm text-neutral-500">{a.email} · {a.placement} · {a.weeks}wk{a.price ? ` · $${a.price}` : ''}</span>
                <span className="ml-auto text-xs text-neutral-400">{a.impressions} views · {a.clicks} clicks</span>
              </div>
              <img src={a.image_data} alt={`${a.company_name} banner`} className="w-full max-h-28 object-cover border border-neutral-200 mb-3" />
              <div className="flex flex-wrap items-center gap-2">
                <a href={a.target_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#D95800] underline truncate max-w-xs">{a.target_url}</a>
                {a.stats_token && (
                  <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/ad-stats/${a.stats_token}`); toast({ title: 'Stats link copied — send it to the advertiser' }); }}
                    className="h-9 px-3 text-xs font-bold bg-neutral-100 border-2 border-neutral-300 hover:bg-neutral-200"
                    data-testid={`copy-stats-${a.id}`}>
                    Copy Stats Link
                  </button>
                )}
                <span className="ml-auto flex flex-wrap gap-2">
                  {(a.status === 'pending_approval' || a.status === 'rejected') && (
                    <button onClick={() => review(a.id, 'approve')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-emerald-600 text-white border-2 border-black" data-testid={`approve-ad-${a.id}`}>
                      <CheckCircle size={14} weight="bold" /> Approve & Go Live
                    </button>
                  )}
                  {a.status === 'pending_approval' && (
                    <button onClick={() => review(a.id, 'reject')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-red-50 text-red-600 border-2 border-red-300" data-testid={`reject-ad-${a.id}`}>
                      <XCircle size={14} weight="bold" /> Reject
                    </button>
                  )}
                  {a.status === 'active' && (
                    <button onClick={() => review(a.id, 'pause')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-blue-50 text-blue-700 border-2 border-blue-300" data-testid={`pause-ad-${a.id}`}>
                      <Pause size={14} weight="bold" /> Pause
                    </button>
                  )}
                  {a.status === 'paused' && (
                    <button onClick={() => review(a.id, 'resume')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-emerald-50 text-emerald-700 border-2 border-emerald-300" data-testid={`resume-ad-${a.id}`}>
                      <Play size={14} weight="bold" /> Resume
                    </button>
                  )}
                  {['active', 'paused', 'expired'].includes(a.status) && (
                    <button onClick={() => review(a.id, 'extend', 4)} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-neutral-100 border-2 border-neutral-300 hover:bg-neutral-200" data-testid={`extend-ad-${a.id}`}>
                      <CalendarPlus size={14} weight="bold" /> +4 wks
                    </button>
                  )}
                  <button onClick={() => review(a.id, 'delete')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-red-600 text-white border-2 border-black" data-testid={`delete-ad-${a.id}`}>
                    <Trash size={14} weight="bold" /> Delete
                  </button>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAds;
